import { ChevronDownIcon, SecretIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { useGetSecretsQuery } from '@generated/graphql';
import { Box, Chip, Collapse, Typography, alpha, useTheme } from '@mui/material';
import { useState } from 'react';

interface SecretsChipBarProps {
  organizationId: string;
  workspaceId?: string;
  onInsertSecret: (ref: string) => void;
}

type SecretGroup = {
  scope: string;
  /** Accent color for this group, resolved from the theme palette */
  accentColor: string;
  secrets: Array<{ secretId: string; name: string }>;
};

export const SecretsChipBar = ({
  organizationId,
  workspaceId,
  onInsertSecret,
}: SecretsChipBarProps) => {
  const { palette } = useTheme();
  const [expanded, setExpanded] = useState(false);

  const { data: orgData } = useGetSecretsQuery({
    variables: { organizationId },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: workspaceData } = useGetSecretsQuery({
    variables: { organizationId, workspaceId },
    skip: !organizationId || !workspaceId,
    fetchPolicy: 'cache-and-network',
  });

  const { data: userData } = useGetSecretsQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Org → blue-ish accent from the custom icon.info palette (#7F92D4 in both themes)
  // User → green accent from primary.main (#91E600)
  const orgColor = palette.resource.organization.foreground;
  const userColor = palette.resource.user.foreground;
  const workspaceColor = palette.resource.workspace.foreground;

  const groups: SecretGroup[] = [];

  const orgSecrets = (orgData?.getSecrets || []).filter((s) => s?.name && s.includeInAllWorkspaces);
  if (orgSecrets.length > 0) {
    groups.push({
      scope: 'Org',
      accentColor: orgColor,
      secrets: orgSecrets.map((s) => ({ secretId: s.secretId, name: s.name })),
    });
  }

  const workspaceSecrets = (workspaceData?.getSecrets || []).filter((s) =>
    s.workspaceIds?.includes(workspaceId),
  );
  if (workspaceSecrets.length > 0) {
    groups.push({
      scope: 'Workspace',
      accentColor: workspaceColor,
      secrets: workspaceSecrets.map((s) => ({ secretId: s.secretId, name: s.name })),
    });
  }

  const userSecrets = (userData?.getSecrets || []).filter((s) => s?.name);
  if (userSecrets.length > 0) {
    groups.push({
      scope: 'User',
      accentColor: userColor,
      secrets: userSecrets.map((s) => ({ secretId: s.secretId, name: s.name })),
    });
  }

  if (groups.length === 0) return null;

  const totalSecrets = groups.reduce((sum, g) => sum + g.secrets.length, 0);

  return (
    <Box
      sx={{
        border: `1px solid ${palette.divider}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        backgroundColor: palette.background.paperCard,
      }}
    >
      <Group
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          cursor: 'pointer',
          userSelect: 'none',
          color: palette.foreground.paperCard.light,
          '&:hover': { backgroundColor: alpha(palette.text.primary, 0.04) },
        }}
      >
        <Group alignItems="center" gap={1}>
          <SecretIcon size={14} />
          <Typography
            variant="caption2"
            sx={{ fontWeight: 600, fontSize: '0.75rem', lineHeight: '20px' }}
          >
            Available Secrets
          </Typography>
          <Typography variant="caption2" sx={{ fontSize: '0.7rem', lineHeight: '20px' }}>
            ({totalSecrets}) — click to insert
          </Typography>
        </Group>
        <ChevronDownIcon
          size={14}
          style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </Group>
      <Collapse in={expanded}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 2,
            px: 1.5,
            pb: 1,
            maxHeight: 120,
            overflowY: 'auto',
          }}
        >
          {groups.map((group) => (
            <>
              <Typography
                variant="caption2"
                sx={{
                  minWidth: 32,
                  mt: '3px',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: group.accentColor,
                  lineHeight: '18px',
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                {group.scope}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {group.secrets.map((secret) => (
                  <Chip
                    key={secret.secretId}
                    label={secret.name}
                    size="small"
                    variant="outlined"
                    onClick={() => onInsertSecret(`\${secrets.${secret.name}}`)}
                    sx={{
                      height: 22,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: 'transparent',
                      borderColor: alpha(group.accentColor, 0.5),
                      borderWidth: '1px',
                      '& .MuiChip-label': {
                        fontFamily: '"IBM Plex Mono", "Roboto Mono", monospace',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        lineHeight: '20px',
                        px: 1,
                        color: palette.text.primary,
                      },
                      '&.MuiChip-clickable:hover': {
                        backgroundColor: alpha(group.accentColor, 0.12),
                        borderColor: group.accentColor,
                      },
                    }}
                  />
                ))}
              </Box>
            </>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
